"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const testimonials = [
{
  quote:
  "I used to keep Luna's vet records in three different apps and a drawer full of papers. DogFob put everything in one place and I actually feel prepared now when I visit the clinic.",
  name: "Priya Sharma",
  role: "DogFob owner, Austin TX",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_196e6d6da-1768293814059.png",
  avatarAlt: "Priya Sharma, smiling woman with dark hair, dogfob owner in Austin Texas",
  stars: 5,
  tag: "Pet Owner",
  tagColor: "#5EBFB3",
  tagBg: "#E8F7F6"
},
{
  quote:
  "As a mobile groomer I see 8 dogfobs a day. The DogFob partner profile tells me exactly what shampoo each dogfob needs, any skin conditions, and whether they're nervous around clippers. Game changer.",
  name: "Marcus Webb",
  role: "Mobile groomer, Chicago IL",
  avatar: "https://images.unsplash.com/photo-1625279138836-e7311d5c863a",
  avatarAlt: "Marcus Webb, man with short hair smiling, professional mobile groomer in Chicago",
  stars: 5,
  tag: "DogFob Partner",
  tagColor: "#7C3AED",
  tagBg: "#EDE9FE"
},
{
  quote:
  "My cat Mochi got out one night and I was panicking. Because I'd already set up his profile and connected his tracker through DogFob, I had his live location in seconds. I found him in 12 minutes.",
  name: "Kenji Nakamura",
  role: "Cat owner, Seattle WA",
  avatar: "https://images.unsplash.com/photo-1590155373871-980e777720dc",
  avatarAlt: "Kenji Nakamura, young man with glasses looking at camera, cat owner in Seattle",
  stars: 5,
  tag: "Pet Owner",
  tagColor: "#5EBFB3",
  tagBg: "#E8F7F6"
},
{
  quote:
  "We integrated DogFob into our veterinary practice three months ago. Client no-shows are down 34% because the automated reminders actually work. The onboarding took one afternoon.",
  name: "Dr. Amara Osei",
  role: "Veterinarian, Brooklyn NY",
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_13e1df70a-1763301872470.png",
  avatarAlt: "Dr. Amara Osei, professional woman in medical setting, veterinarian in Brooklyn",
  stars: 5,
  tag: "DogFob Partner",
  tagColor: "#7C3AED",
  tagBg: "#EDE9FE"
}];


export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up, .scroll-scale").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white dark:bg-[#0f172a]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-14 scroll-fade-up">
          <span className="teal-badge mb-4">Early Adopters</span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-[#1A2B2B] dark:text-slate-100 mt-4 mb-4 tracking-tight">
            Real pets. Real stories.
          </h2>
          <p className="text-[#4A6363] dark:text-slate-400 text-lg max-w-lg mx-auto">
            Here's what our beta community — 2,400+ pet owners and 180+ partner businesses — says about DogFob.
          </p>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-14">
          {[
          { value: "2,400+", label: "Pets registered", emoji: "🐾" },
          { value: "180+", label: "Partner businesses", emoji: "🏥" },
          { value: "98%", label: "Owner satisfaction", emoji: "⭐" },
          { value: "12 min", label: "Avg. lost pet recovery", emoji: "📍" }]?.
          map((stat, idx) =>
          <div
            key={stat?.label}
            className={`scroll-scale stagger-${idx + 1} feature-card p-5 text-center transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-xl`}>
            
              <span className="text-2xl mb-2 block">{stat?.emoji}</span>
              <p className="text-3xl font-extrabold text-[#1A2B2B] dark:text-slate-100 font-heading mb-1">{stat?.value}</p>
              <p className="text-xs text-[#8AA4A4] dark:text-slate-400 font-medium">{stat?.label}</p>
            </div>
          )}
        </div>

        {/* Testimonial cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {testimonials?.map((t, idx) =>
          <div
            key={`${t?.name}-${idx}`}
            className={`scroll-fade-up stagger-${idx + 1} feature-card p-7 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-xl`}>
            
              {/* Stars */}
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t?.stars })?.map((_, si) =>
              <Icon key={si} name="StarIcon" size={14} style={{ color: "#FBBF24" }} variant="solid" />
              )}
              </div>

              <blockquote className="text-[#1A2B2B] dark:text-slate-100 leading-relaxed mb-6 text-[15px]">
                "{t?.quote}"
              </blockquote>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <AppImage
                    src={t?.avatar}
                    alt={t?.avatarAlt}
                    width={40}
                    height={40}
                    className="w-full h-full object-cover" />
                  
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#1A2B2B] dark:text-slate-100 font-heading">{t?.name}</p>
                    <p className="text-xs text-[#8AA4A4] dark:text-slate-400">{t?.role}</p>
                  </div>
                </div>
                <span
                className="text-[11px] font-bold px-3 py-1 rounded-full"
                style={{ background: t?.tagBg, color: t?.tagColor }}>
                
                  {t?.tag}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}